import React from "react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white pt-24 pb-16">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-8">Terms of Service</h1>
          <p className="text-gray-400 mb-10">Last updated: March 20, 2025</p>
          
          <div className="prose prose-lg prose-invert max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Introduction</h2>
            <p>
              Welcome to Escrownia ("Platform", "we", "us", "our"). By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
            </p>
            <p>
              Escrownia provides a blockchain-based escrow service that allows buyers and sellers to enter into secure transactions using smart contracts. These Terms govern your use of our platform and services.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>User:</strong> Any individual or entity that accesses or uses the Platform.
              </li>
              <li>
                <strong>Buyer:</strong> A User who deposits funds into an escrow smart contract.
              </li>
              <li>
                <strong>Seller:</strong> A User who is designated to receive funds from an escrow smart contract upon fulfillment of agreed conditions.
              </li>
              <li>
                <strong>Arbiter:</strong> A neutral third party who resolves disputes between Buyers and Sellers.
              </li>
              <li>
                <strong>Smart Contract:</strong> A self-executing contract with the terms of the agreement directly written into code.
              </li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">3. User Registration and Eligibility</h2>
            <p>
              To use our Platform, you must:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be at least 18 years old</li>
              <li>Have a compatible cryptocurrency wallet</li>
              <li>Provide accurate and complete information if requested</li>
              <li>Not be a resident of a jurisdiction where use of our services would be illegal</li>
            </ul>
            <p>
              We reserve the right to refuse service to anyone for any reason at any time.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Platform Services</h2>
            <p>
              Escrownia provides the following services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Creation of escrow smart contracts for secure transactions</li>
              <li>Holding funds in escrow until predefined conditions are met</li>
              <li>Automated release of funds when conditions are fulfilled</li>
              <li>Dispute resolution through independent arbiters</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Fees</h2>
            <p>
              Escrownia charges fees for its services as described on our website. Fees are subject to change with notice. In addition to our fees, users are responsible for any network transaction fees (gas fees) required to interact with the blockchain.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">6. Smart Contracts</h2>
            <p>
              Users acknowledge that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Smart contracts operate according to their code and cannot be modified once deployed</li>
              <li>Funds locked in smart contracts are controlled by the contract code, not by Escrownia</li>
              <li>Users are responsible for understanding the terms and conditions of each smart contract they enter into</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Dispute Resolution</h2>
            <p>
              If a dispute arises between Buyer and Seller:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Either party may initiate a dispute through the Platform</li>
              <li>An independent Arbiter will review the case and evidence</li>
              <li>The Arbiter's decision is final and binding</li>
              <li>Dispute resolution fees apply and will be disclosed before initiating a dispute</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">8. User Responsibilities</h2>
            <p>
              Users agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Platform in compliance with all applicable laws and regulations</li>
              <li>Not engage in any fraudulent, deceptive, or harmful activities</li>
              <li>Maintain the security of their wallet and private keys</li>
              <li>Provide truthful and accurate information in all transactions and disputes</li>
              <li>Not use the Platform for illegal purposes, including money laundering, terrorist financing, or fraud</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Limitations of Liability</h2>
            <p>
              To the maximum extent permitted by law, Escrownia shall not be liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Issues arising from blockchain network failures or delays</li>
              <li>Smart contract vulnerabilities or bugs not resulting from our negligence</li>
              <li>User errors, including sending funds to incorrect addresses</li>
              <li>Actions of third parties, including other users</li>
            </ul>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Modifications to the Terms</h2>
            <p>
              Escrownia reserves the right to modify these Terms at any time. We will provide notice of significant changes through the Platform or by other means. Your continued use of the Platform after such changes constitutes acceptance of the modified Terms.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Termination</h2>
            <p>
              We may terminate or suspend your access to the Platform immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Platform will cease immediately.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction], without regard to its conflict of law provisions.
            </p>
            
            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> legal@escrownia.com<br />
              <strong>Address:</strong> 123 Blockchain Street, Tech City, 12345
            </p>
            
            <div className="mt-12 p-6 bg-gray-800 rounded-lg border border-gray-700">
              <h3 className="text-xl font-semibold mb-4">Acceptance of Terms</h3>
              <p>
                By using Escrownia, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
              
              <div className="mt-6 flex justify-between items-center">
                <Link to="/" className="text-blue-400 hover:text-blue-300 transition-colors">
                  Return to Home
                </Link>
                
                <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300 transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;